import { useEffect, useRef, useState } from "preact/hooks"
import arrowDown from "@assets/home/arrow-down.svg?url"
import clsx from "clsx"
import { JSX } from "preact/jsx-runtime"
import { track } from "@client/analytics"

export interface Option {
  key: string
  value: string
  enrolled: boolean
}

export interface SelectInputProps {
  label: string
  options: Option[]
  defaultValue?: Option
  onChange?: (value: Option) => void
  disabled?: boolean
}

const StatesInput = ({
  label,
  options,
  defaultValue,
  onChange = () => {},
  disabled = false,
}: SelectInputProps): JSX.Element => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState<Option | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const toggleDropdown = () => {
    if (disabled) return
    setDropdownOpen(!dropdownOpen)
  }

  const selectOption = (option: Option) => {
    track("Region Selected", {
      region: option.value,
    })
    setSelectedValue(option)
    onChange(option)
    setDropdownOpen(false)
  }

  useEffect(() => {
    const clickOutsideDropdown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", clickOutsideDropdown)
    return () => {
      document.removeEventListener("mousedown", clickOutsideDropdown)
    }
  }, [])

  useEffect(() => {
    if (defaultValue) setSelectedValue(defaultValue)
  }, [defaultValue, options])

  return (
    <div className="relative" ref={containerRef}>
      <input type="hidden" value={selectedValue?.key || ""} />
      <button
        type="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={dropdownOpen}
        onClick={toggleDropdown}
        className={clsx(
          "px-4 py-1.5 bg-white rounded-xl shadow-[0px_1px_2px_0px_rgba(86,119,159,0.07)] shadow-[0px_3px_3px_0px_rgba(86,119,159,0.06)] shadow-[0px_7px_4px_0px_rgba(86,119,159,0.04)] shadow-[0px_12px_5px_0px_rgba(86,119,159,0.01)] shadow-[0px_19px_5px_0px_rgba(86,119,159,0.00)] outline outline-1 outline-offset-[-1px] outline-gray-200 inline-flex justify-between items-center gap-3 cursor-pointer select-none",
          {
            "opacity-50 pointer-events-none": disabled,
          },
        )}
      >
        {selectedValue && (
          <div className="text-center justify-center text-neutral-60-primary-text text-3xl lg:text-4xl font-semibold leading-[48px]">
            {selectedValue.value || label}
          </div>
        )}
        <img src={arrowDown} />
      </button>
      {dropdownOpen && (
        <ul
          className={clsx("absolute top-0 left-1/2 -translate-x-1/2 z-30 py-2")}
        >
          <li className="px-2 bg-white border border-gray-200 rounded-xl w-60 shadow-lg max-h-[456px] overflow-y-auto">
            {options.map((option) => (
              <div
                key={option.key}
                className={clsx(
                  "px-4 py-2 cursor-pointer hover:bg-gray-100 text-base rounded-md",
                  {
                    "text-blue-30-primary bg-blue-0":
                      selectedValue?.key === option.key,
                    "text-neutral-60-primary-text":
                      selectedValue?.key !== option.key,
                  },
                )}
                onClick={() => selectOption(option)}
              >
                {option.value}
              </div>
            ))}
          </li>
        </ul>
      )}
    </div>
  )
}

export default StatesInput
